import { clearUserAuthStorage, setStorageItem } from '@/shared/helpers/storage';
import { PUBLIC_USER_CREDENTIALS, TOKEN_COOKIE_NAMES } from '@/shared/utils/constants';
import { camelizeNestedKeys } from '@cex/shared-modules';
import { EntityAdapter, EntityState, createEntityAdapter } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import env from '@beam-australia/react-env';

type OAuthDataFE = {
  expiresIn: number;
  tokenType: string;
  accessToken: string;
  refreshToken: string;
};

type OAuthDataBE = {
  expires_in: number;
  token_type: string;
  access_token: string;
  refresh_token: string;
};

export type LoginParams = {
  code: string;
  state: string;
  userSpecificClientId: string;
  userSpecificClientSecret: string;
};

const authAdapter: EntityAdapter<any> = createEntityAdapter<OAuthDataFE>({
  selectId: (auth) => auth.accessToken
});
const initialState = authAdapter.getInitialState();

export const authApi = createApi({
  reducerPath: 'auth',
  baseQuery: fetchBaseQuery({
    baseUrl: env('OAUTH_BASE_URL')
  }),
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    handleRefreshingTheToken: builder.mutation<
      EntityState<OAuthDataFE>,
      {
        creds: LoginParams | null;
        refreshToken: string | null;
      }
    >({
      query({ creds, refreshToken }) {
        const userSpecificClientId = creds?.userSpecificClientId;
        const userSpecificClientSecret = creds?.userSpecificClientSecret;

        return {
          url: `api/v2/oauth/token`,
          method: 'POST',
          body: {
            client_id: userSpecificClientId,
            client_secret: userSpecificClientSecret,
            grant_type: 'refresh_token',
            refresh_token: refreshToken
          }
        };
      },
      transformResponse: (res: OAuthDataBE) => {
        const camelizedResponse = camelizeNestedKeys(res);
        const { accessToken, refreshToken } = camelizedResponse;
        setStorageItem(TOKEN_COOKIE_NAMES['access'], accessToken);
        setStorageItem(TOKEN_COOKIE_NAMES['refresh'], refreshToken);

        return authAdapter.setAll(initialState, camelizedResponse);
      },
      transformErrorResponse: (error) => {
        console.error('handleRefreshingTheToken', error);
        clearUserAuthStorage();
        return authAdapter.removeAll(initialState);
      }
    }),
    handleOAuthRedirect: builder.mutation({
      query({ code, userSpecificClientId, userSpecificClientSecret }) {
        const pubCreds = JSON.stringify({
          userSpecificClientId,
          userSpecificClientSecret
        });
        setStorageItem(PUBLIC_USER_CREDENTIALS, pubCreds);

        return {
          url: `api/v2/oauth/token`,
          method: 'POST',
          body: {
            client_id: userSpecificClientId,
            client_secret: userSpecificClientSecret,
            code,
            grant_type: 'authorization_code'
          }
        };
      },
      transformResponse: (res: OAuthDataBE) => {
        const camelizedResponse = camelizeNestedKeys(res);
        const { accessToken, refreshToken } = camelizedResponse;

        setStorageItem(TOKEN_COOKIE_NAMES['access'], accessToken);
        setStorageItem(TOKEN_COOKIE_NAMES['refresh'], refreshToken);

        return authAdapter.setOne(initialState, camelizedResponse);
      },
      transformErrorResponse: (error) => {
        console.error(error);
      }
    })
  })
});

export const { useHandleOAuthRedirectMutation, useHandleRefreshingTheTokenMutation } = authApi;
